import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ExploreArtistsResponseSchema,
  ExplorePlaylistsResponseSchema,
  type ExploreArtistsResponse,
  type ExplorePlaylistKind,
  type ExplorePlaylistsResponse,
} from "#shared/schemas/playlistExplore.ts";
import { api, ApiRequestError } from "@web/lib/api.ts";
import { toast } from "sonner";

function failToast(err: unknown, fallback: string): void {
  toast.error(err instanceof ApiRequestError ? err.message : fallback);
}

export function useExplorePlaylistArtists(
  kind: ExplorePlaylistKind,
  id: string,
  offset: number,
  limit = 25,
  enabled = true,
) {
  return useQuery({
    queryKey: ["graph", "explore", "artists", kind, id, offset, limit],
    enabled: enabled && id.length > 0,
    queryFn: async (): Promise<ExploreArtistsResponse> => {
      const sp = new URLSearchParams({
        kind,
        id,
        offset: String(offset),
        limit: String(limit),
      });
      return ExploreArtistsResponseSchema.parse(
        await api(`/api/graph/explore/artists?${sp}`),
      );
    },
  });
}

export function fetchExplorePlaylistArtists(
  kind: ExplorePlaylistKind,
  id: string,
  offset: number,
  limit = 25,
): Promise<ExploreArtistsResponse> {
  const sp = new URLSearchParams({
    kind,
    id,
    offset: String(offset),
    limit: String(limit),
  });
  return api(`/api/graph/explore/artists?${sp}`).then((body) =>
    ExploreArtistsResponseSchema.parse(body),
  );
}

export function fetchExploreArtistPlaylists(
  artistId: string,
  offset: number,
  limit = 25,
): Promise<ExplorePlaylistsResponse> {
  const sp = new URLSearchParams({
    id: artistId,
    offset: String(offset),
    limit: String(limit),
  });
  return api(`/api/graph/explore/playlists?${sp}`).then((body) =>
    ExplorePlaylistsResponseSchema.parse(body),
  );
}

/** Prefetch seed page so the page header can show the label while the canvas boots. */
export function useExploreSeedPreview(kind: ExplorePlaylistKind, id: string) {
  return useExplorePlaylistArtists(kind, id, 0, 1, true);
}

export function useExploreExpandMutation() {
  return useMutation({
    mutationFn: async (
      input:
        | { type: "playlist-artists"; kind: ExplorePlaylistKind; id: string; offset: number; limit?: number }
        | { type: "artist-playlists"; id: string; offset: number; limit?: number },
    ) => {
      if (input.type === "playlist-artists") {
        return {
          type: "playlist-artists" as const,
          data: await fetchExplorePlaylistArtists(
            input.kind,
            input.id,
            input.offset,
            input.limit ?? 25,
          ),
        };
      }
      return {
        type: "artist-playlists" as const,
        data: await fetchExploreArtistPlaylists(input.id, input.offset, input.limit ?? 25),
      };
    },
    onError: (err) => failToast(err, "Failed to expand node"),
  });
}
